<script setup lang="ts">
import isIos from 'is-ios'
import { BarMitzvahType } from 'types/types'
import { computed, ref, watch } from 'vue'

import AnnotatedText from '@/components/AnnotatedText.vue'
import ColorationToolbar from '@/components/ColorationToolbar.vue'
import PopOver from '@/components/PopOver.vue'
import { useFont } from '@/composables/font'
import { formattedDate, practiceRecordingDescription, practiceRecordingSettings } from '@/composables/format'
import { useAnnotations } from '@/composables/useAnnotations'
import { usePlaybackOverlay } from '@/composables/usePlaybackOverlay'
import { useTextColoration } from '@/composables/useTextColoration'
import {
  FONT_SIZE_STEP,
  OVERLAY_SLIDER_MAX_WIDTH_PX,
  OVERLAY_SLIDER_MIN_WIDTH_PX,
  PLAYBACK_OVERLAY_TEXT_NAME,
  TOOLTIP_OPEN_DELAY_MS,
} from '@/constants/practiceConstants'
import { DEFAULT_LINE_HEIGHT, MAX_FONT_SIZE, MIN_FONT_SIZE } from '@/util/FontUtil'

const props = defineProps<{
  barMitzvah?: BarMitzvahType | null
  studentName?: string
}>()

const {
  audioSrc,
  canJumpNext,
  canJumpPrev,
  clearPendingChoice,
  clickableSegment,
  closeOverlay: closePlayback,
  currentSentenceIndex,
  downloadRecording,
  handleSegmentClick,
  jumpFirst,
  jumpLast,
  jumpNext,
  jumpPrev,
  loading,
  maxSurroundLines,
  onAudioTimeUpdate,
  openOverlay,
  overlayFontSize,
  pendingOccurrenceChoice,
  recording,
  seekGeneration,
  seekTimeMs,
  seekToOccurrence,
  segmentStyle,
  segmentations,
  sentenceStyle,
  startIndex,
  suppressColors,
  surroundLines,
  totalSentences,
  visible,
  visibleSentences,
} = usePlaybackOverlay()

// --- Font & Annotations ---
const { font } = useFont()
const barMitzvahRef = computed(() => props.barMitzvah ?? null)
const { annotations } = useAnnotations(barMitzvahRef)

// --- Coloration ---

const coloration = useTextColoration({
  barMitzvah: barMitzvahRef,
  disabled: computed(() => false),
  sentenceIds: computed(() => (visibleSentences.value.length ? visibleSentences.value.map((s) => s.id) : null)),
})

// When coloration is active, suppress segment click (used for seek) and enable char-level interaction
const effectiveClickableSegment = computed(() => (coloration.active.value ? null : clickableSegment))
const effectiveClickableChar = computed(() => (coloration.active.value ? () => true : null))
const effectiveDraggableChar = computed(() => (coloration.active.value ? () => true : null))
const guardedSegmentClick = (...args: Parameters<typeof handleSegmentClick>) => {
  if (coloration.active.value) {
    return
  }
  if (!clickableSegment(...args)) {
    return
  }
  handleSegmentClick(...args)
}

// Hide disambiguation popup when colorization is enabled
watch(
  () => coloration.active.value,
  (active) => {
    if (active) {
      clearPendingChoice()
    }
  },
)

// Close coloration when overlay closes
watch(visible, (isVisible) => {
  if (!isVisible && coloration.active.value) {
    coloration.toggleColoration()
  }
})

const audioRef = ref<HTMLAudioElement | null>(null)
const isPlaying = ref(false)

// Pause playback when entering coloration mode
watch(
  () => coloration.active.value,
  (active) => {
    if (active && audioRef.value && !audioRef.value.paused) {
      audioRef.value.pause()
      isPlaying.value = false
    }
  },
)

const description = computed(() => (recording.value ? practiceRecordingDescription(recording.value) : ''))
const title = computed(() => (props.studentName ? `${props.studentName}, ${description.value}` : description.value))
const date = computed(() => (recording.value ? formattedDate(recording.value.created) : ''))
const settings = computed(() => (recording.value ? practiceRecordingSettings(recording.value) : ''))

const sentenceLabel = computed(() => `${currentSentenceIndex.value + 1} / ${totalSentences.value}`)
const isFirst = computed(() => currentSentenceIndex.value === 0)
const isLast = computed(() => currentSentenceIndex.value === totalSentences.value - 1)

function onTimeUpdate() {
  if (audioRef.value) {
    onAudioTimeUpdate(audioRef.value.currentTime * 1000)
  }
}

function togglePlayPause() {
  if (!audioRef.value) {
    return
  }
  if (audioRef.value.paused) {
    audioRef.value.play()
    isPlaying.value = true
  } else {
    audioRef.value.pause()
    isPlaying.value = false
  }
}

function restart() {
  if (!audioRef.value) {
    return
  }
  audioRef.value.currentTime = 0
  audioRef.value.play()
  isPlaying.value = true
}

function closeOverlay() {
  if (audioRef.value) {
    audioRef.value.pause()
  }
  isPlaying.value = false
  closePlayback()
}

// Seek audio when the composable requests it
watch(seekGeneration, () => {
  if (audioRef.value) {
    audioRef.value.currentTime = seekTimeMs.value / 1000
    if (audioRef.value.paused) {
      audioRef.value.play()
      isPlaying.value = true
    }
  }
})

// Sync play state: autoplay starts when audio loads (loading becomes false)
watch(loading, (isLoading) => {
  if (!isLoading && visible.value && audioSrc.value) {
    isPlaying.value = true
  }
})

// Pause audio when overlay is hidden
watch(visible, (isVisible) => {
  if (!isVisible && audioRef.value) {
    audioRef.value.pause()
    isPlaying.value = false
  }
})

defineExpose({ closeOverlay, downloadRecording, openOverlay })
</script>

<template>
  <v-dialog :model-value="visible" scrim="black" width="80vw" @keydown.esc="closeOverlay" @update:model-value="!$event && closeOverlay()">
    <v-card class="d-flex flex-column playback-card" :ripple="false" style="height: 80vh" @click="coloration.handleClickOutside">
      <!-- Header -->
      <v-card-title class="d-flex align-center pa-4">
        <div>
          <span class="text-h6">
            {{ title }}
            <span class="text-body-2 text-medium-emphasis ml-2">{{ date }}</span>
          </span>
          <div v-if="settings" class="text-body-2 text-medium-emphasis">{{ settings }}</div>
        </div>
        <v-spacer />
        <v-btn icon variant="text" @click="closeOverlay">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <!-- Loading indicator -->
      <v-progress-linear v-if="loading" color="primary" indeterminate />

      <!-- Controls -->
      <div class="px-6 py-2 d-flex align-center ga-6 flex-wrap">
        <!-- Transport controls -->
        <div class="d-flex align-center ga-1">
          <v-btn :disabled="loading" icon size="small" variant="text" @click="restart">
            <v-icon>mdi-skip-backward</v-icon>
          </v-btn>
          <v-btn :disabled="loading" icon size="small" variant="text" @click="togglePlayPause">
            <v-icon>{{ isPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
          </v-btn>
        </div>

        <!-- Sentence navigation (matches PracticeOverlay style) -->
        <template v-if="totalSentences > 1">
          <div class="d-flex align-center ga-1">
            <v-btn :disabled="isFirst" size="small" @click="jumpFirst">
              <v-icon color="primary" size="small">mdi-page-first</v-icon>
              First
            </v-btn>
            <v-btn :disabled="!canJumpPrev" size="small" @click="jumpPrev">
              <v-icon color="primary" size="small">mdi-skip-backward</v-icon>
              Prev
            </v-btn>
          </div>

          <span class="text-caption text-medium-emphasis text-no-wrap">{{ sentenceLabel }}</span>

          <div class="d-flex align-center ga-1">
            <v-btn :disabled="!canJumpNext" size="small" @click="jumpNext">
              Next
              <v-icon color="primary" size="small">mdi-skip-forward</v-icon>
            </v-btn>
            <v-btn :disabled="isLast" size="small" @click="jumpLast">
              Last
              <v-icon color="primary" size="small">mdi-page-last</v-icon>
            </v-btn>
          </div>
        </template>

        <!-- Style button -->
        <v-tooltip
          v-if="!isIos"
          bottom
          :open-delay="TOOLTIP_OPEN_DELAY_MS"
          :text="barMitzvah ? 'Style text with colors, sizes, and formatting' : 'Styling only works after you select a student'"
        >
          <template #activator="{ props: tipProps }">
            <span v-bind="tipProps">
              <v-btn
                :active="coloration.active.value"
                class="colorize-btn"
                :disabled="!barMitzvah"
                :variant="coloration.active.value ? 'flat' : 'outlined'"
                @click="coloration.toggleColoration"
              >
                <v-icon left size="small">mdi-brush</v-icon>
                Style
              </v-btn>
            </span>
          </template>
        </v-tooltip>

        <div
          class="d-flex align-center ga-2"
          :style="{ minWidth: `${OVERLAY_SLIDER_MIN_WIDTH_PX}px`, maxWidth: `${OVERLAY_SLIDER_MAX_WIDTH_PX}px` }"
        >
          <span class="text-caption text-medium-emphasis text-no-wrap">Size</span>
          <v-slider
            v-model="overlayFontSize"
            color="primary"
            hide-details
            :max="MAX_FONT_SIZE"
            :min="MIN_FONT_SIZE"
            :step="FONT_SIZE_STEP"
          />
        </div>

        <div
          v-if="maxSurroundLines > 0"
          class="d-flex align-center ga-2"
          :style="{ minWidth: `${OVERLAY_SLIDER_MIN_WIDTH_PX}px`, maxWidth: `${OVERLAY_SLIDER_MAX_WIDTH_PX}px` }"
        >
          <span class="text-caption text-medium-emphasis text-no-wrap">Context</span>
          <v-slider v-model="surroundLines" color="primary" hide-details :max="maxSurroundLines" :min="0" :step="1" thumb-label />
        </div>
      </div>

      <!-- Coloration Toolbar (color pickers, shown when active) -->
      <!-- @click.stop prevents color picker clicks from bubbling to the card's handleClickOutside -->
      <coloration-toolbar
        :active="coloration.active.value"
        :background-color="coloration.backgroundColor.value"
        :bar-mitzvah="barMitzvah ?? null"
        :font-size="coloration.fontSize.value"
        :foreground-color="coloration.foregroundColor.value"
        :has-selection="coloration.hasSelection.value"
        :underline="coloration.underline.value"
        @clear-all-colors="coloration.clearAllColors"
        @clear-all-whitespace="coloration.clearAllWhitespace"
        @clear-selection="coloration.clearSelectedAnnotations"
        @click.stop
        @close="coloration.toggleColoration"
        @update-background="(c) => coloration.applyColor('backgroundColor', c)"
        @update-font-size="(s) => coloration.applyColor('fontSize', s)"
        @update-foreground="(c) => coloration.applyColor('color', c)"
        @update-underline="coloration.applyUnderline"
      />

      <v-divider />

      <!-- Hebrew text area -->
      <v-card-text class="flex-grow-1 overflow-y-auto pa-6">
        <annotated-text
          v-if="visibleSentences.length"
          :annotations="annotations"
          :break-before-line="() => true"
          :clickable-char="effectiveClickableChar"
          :clickable-segment="effectiveClickableSegment"
          :default-font="font"
          :draggable-char="effectiveDraggableChar"
          :font-size="overlayFontSize"
          :is-char-selected="coloration.active.value ? coloration.isCharSelected.value : null"
          :is-segment-boundary-selected="coloration.active.value ? coloration.isSegmentBoundarySelected.value : null"
          :is-word-boundary-selected="coloration.active.value ? coloration.isWordBoundarySelected.value : null"
          :line-height="DEFAULT_LINE_HEIGHT"
          :name="PLAYBACK_OVERLAY_TEXT_NAME"
          :segment-style="segmentStyle"
          :segmentations="segmentations"
          :sentence-style="sentenceStyle"
          :sentences="visibleSentences"
          :show-sentence-numbers="true"
          :start-index="startIndex"
          :suppress-colors="coloration.active.value ? undefined : suppressColors"
          @click-segment="guardedSegmentClick"
          @dblclick-char="coloration.handleCharDblclick"
          @mousedown-char="coloration.handleCharMousedown"
          @mouseenter-char="coloration.handleCharMouseenter"
          @mouseup-text="coloration.handleMouseup"
        />
        <div v-else class="text-center text-medium-emphasis pa-8">No text available for this recording.</div>
      </v-card-text>

      <!-- Hidden audio element -->
      <audio v-if="audioSrc" ref="audioRef" autoplay :src="audioSrc" @timeupdate="onTimeUpdate" />

      <!-- Occurrence picker (shown when a segment has multiple practice occurrences) -->
      <pop-over :attach-id="pendingOccurrenceChoice?.attachId ?? null" :visible="!!pendingOccurrenceChoice && !coloration.active.value">
        <v-card>
          <v-card-text class="pa-1">
            <v-btn
              v-for="(occ, i) in pendingOccurrenceChoice?.occurrences"
              :key="i"
              size="small"
              variant="text"
              @click="seekToOccurrence(occ.offsetMs)"
            >
              {{ occ.label }}
            </v-btn>
            <v-btn icon size="small" variant="text" @click="clearPendingChoice()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-text>
        </v-card>
      </pop-over>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.playback-card {
  cursor: default;
}
</style>
